import React, { useEffect, useState } from 'react';
import CascaDeModal from '../shared/CascaDeModal';
import { SIGNERS } from '../../config/company';
import { procurementService } from '../../services/api';
import { campo } from '../../design';

/**
 * Modal "Enviar para Assinatura" das declarações e da proposta financeira.
 *
 * Recebe a lista de documentos por PROP (`docs`). Enquanto ele lia o estado
 * `generatedDocs` do LicitacoesTab, a lista vinha sempre vazia quando o modal
 * era aberto pelo workspace: aquele estado só é preenchido dentro do drawer de
 * edição legado, que não é mais alcançável. O resultado era "Nenhum documento
 * em status gerado disponível para envio" com os documentos visíveis logo atrás,
 * no card.
 *
 * Dois caminhos de envio, preservados do editor antigo:
 *  - documento individual (`singleDoc`): assina o PDF que já está no S3, sem
 *    regenerar;
 *  - lote: envia por template, com modo unificado/separado por linha.
 */

export interface SignatureBatchModalProps {
  /** Documento único a assinar; null = envio em lote. */
  singleDoc: any | null;
  /** Documentos gerados da proposta (fonte da lista do lote). */
  docs: any[];
  /** Proposta em foco — o lote é enviado por proposta. */
  proposal: { id?: number; itemId?: number } | null;
  /**
   * Troca de signatário de um documento já enviado: cancela na ClickSign e
   * recria com o mesmo PDF. Só faz sentido com `singleDoc`.
   */
  replaceMode?: boolean;
  onClose: () => void;
  showToast: (message: string, type?: 'success' | 'error') => void;
  /** Chamado após envio bem-sucedido (recarregar a lista de documentos). */
  onSent: () => void | Promise<void>;
}

type AuthType = 'email' | 'whatsapp' | 'icp_brasil';

export default function SignatureBatchModal({
  singleDoc,
  docs,
  proposal,
  replaceMode,
  onClose,
  showToast,
  onSent,
}: SignatureBatchModalProps) {
  const [signerForm, setSignerForm] = useState({
    name: SIGNERS.fabio.name,
    email: SIGNERS.fabio.email,
    cpf: SIGNERS.fabio.cpf,
    birthday: SIGNERS.fabio.birthday,
    authType: 'email' as AuthType,
    phone: SIGNERS.fabio.phone || '',
  });
  const isProposta = (d: any) =>
    d.type === 'proposta_financeira' || d.name === 'Proposta Financeira';

  // A lista sai dos DOCUMENTOS, não dos templates: declaração gerada fora de
  // template (a de Não Vistoria da PGDF, por exemplo) não tem template com que
  // casar e ficava fora do envio.
  const linhas = docs
    .filter((d: any) => d.status === 'gerado' && d.type !== 'pacote_assinatura')
    .map((d: any) => ({ id: d.id as number, label: d.name as string, isProposta: isProposta(d) }));

  const [selectedIds, setSelectedIds] = useState<Set<number>>(
    () => new Set(linhas.map((l) => l.id)),
  );
  const [docMode, setDocMode] = useState<Map<number, 'unified' | 'separate'>>(
    () => new Map(linhas.map((l) => [l.id, l.isProposta ? 'separate' : 'unified'] as const)),
  );
  const [saving, setSaving] = useState(false);

  // Documento que chega DEPOIS da abertura (o card repolla enquanto o modal
  // está aberto) nasce marcado, como os demais. Sem isto, o valor inicial do
  // estado seria a única leitura da lista, e um documento recém-carregado
  // apareceria desmarcado sem nada explicando por quê.
  const idsDisponiveis = linhas.map((l) => l.id).join(',');
  useEffect(() => {
    const ids = linhas.map((l) => l.id);
    setSelectedIds((prev) => {
      const novos = ids.filter((id) => !prev.has(id) && !docMode.has(id));
      return novos.length ? new Set([...Array.from(prev), ...novos]) : prev;
    });
    setDocMode((prev) => {
      const faltando = linhas.filter((l) => !prev.has(l.id));
      if (!faltando.length) return prev;
      const next = new Map(prev);
      faltando.forEach((l) => next.set(l.id, l.isProposta ? 'separate' : 'unified'));
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsDisponiveis]);

  const signerPayload = () => [{
    name: signerForm.name,
    email: signerForm.email,
    cpf: signerForm.cpf || undefined,
    birthday: signerForm.birthday || undefined,
    authType: signerForm.authType,
    phone: signerForm.authType === 'whatsapp' ? signerForm.phone || undefined : undefined,
  }];

  const handleSend = async () => {
    if (!signerForm.name || !signerForm.email) { showToast('Preencha nome e email', 'error'); return; }
    if (signerForm.authType === 'whatsapp' && !signerForm.phone) { showToast('Preencha o WhatsApp', 'error'); return; }
    setSaving(true);
    try {
      // Troca de signatário: cancela na ClickSign e recria com o mesmo PDF.
      if (replaceMode && singleDoc?.id) {
        await procurementService.replaceGeneratedDocSigner(singleDoc.id, signerPayload());
        showToast('Signatário trocado e documento reenviado');
        onClose();
        await onSent();
        return;
      }

      // Documento individual que já tem PDF salvo: assina O ARQUIVO DO CARD.
      // O caminho por template regenera o PDF (layout antigo da proposta) e,
      // quando não existe template com aquele nome, manda lista vazia e a
      // ClickSign não recebe nada — era o "0 enviada(s)" silencioso.
      if (singleDoc?.id) {
        const r = await procurementService.sendGeneratedDocForSignature(singleDoc.id, signerPayload());
        if (r?.errors?.length) showToast(`ClickSign: ${r.errors.join('; ')}`, 'error');
        else showToast('Enviado para assinatura');
        onClose();
        await onSent();
        return;
      }

      if (!proposal?.id) { showToast('Proposta não identificada', 'error'); setSaving(false); return; }
      const escolhidos = linhas.filter((l) => selectedIds.has(l.id));
      if (escolhidos.length === 0) {
        showToast('Selecione pelo menos um documento', 'error');
        setSaving(false);
        return;
      }

      // O modo é por chamada no backend; a tela continua permitindo escolher
      // linha a linha, então os dois grupos viram dois envios.
      const unificados = escolhidos.filter((l) => (docMode.get(l.id) || 'unified') === 'unified').map((l) => l.id);
      const separados = escolhidos.filter((l) => (docMode.get(l.id) || 'unified') === 'separate').map((l) => l.id);

      const resultados: any[] = [];
      if (unificados.length) {
        const r = await procurementService.sendGeneratedDocsForSignature(proposal.id, {
          documentIds: unificados,
          // Um documento só não precisa virar pacote.
          mode: unificados.length > 1 ? 'unified' : 'separate',
          signers: signerPayload(),
        });
        resultados.push(...(r.results || []));
      }
      if (separados.length) {
        const r = await procurementService.sendGeneratedDocsForSignature(proposal.id, {
          documentIds: separados,
          mode: 'separate',
          signers: signerPayload(),
        });
        resultados.push(...(r.results || []));
      }

      const ok = resultados.filter((r: any) => r.documentKey).length;
      const fail = resultados.filter((r: any) => r.error).length;
      showToast(`ClickSign: ${ok} enviada(s)${fail ? `, ${fail} erro(s)` : ''}`, fail ? 'error' : 'success');
      onClose();
      await onSent();
    } catch (e: any) {
      showToast('Erro ClickSign: ' + (e.response?.data?.message || e.message), 'error');
    } finally {
      setSaving(false);
    }
  };

  let unifiedCount = 0;
  let separateCount = 0;
  linhas.filter((l) => selectedIds.has(l.id)).forEach((l) => {
    if ((docMode.get(l.id) || 'unified') === 'unified') unifiedCount++; else separateCount++;
  });
  const totalDocs = (unifiedCount > 1 ? 1 : unifiedCount) + separateCount;

  return (
    <div onClick={(e) => e.stopPropagation()}>
    {/* Signatário digitado: não fecha ao clicar fora, como já era. Envio para fora
        (ClickSign): só moldura, handleSend não muda. O wrapper mantém o
        stopPropagation do overlay antigo. A primária roxa vira o laranja. */}
    <CascaDeModal
      largura="xs"
      zIndex="z-[60]"
      titulo={replaceMode ? 'Trocar Signatário' : 'Enviar para Assinatura'}
      onClose={onClose}
      corpoClassName="p-6 space-y-4"
      rodape={
        <div className="ml-auto flex gap-3">
          <button onClick={onClose} className="px-4 py-2.5 text-sm font-medium text-slate-300 bg-slate-700 rounded-lg hover:bg-slate-600">
            Cancelar
          </button>
          <button
            onClick={handleSend}
            disabled={saving || !signerForm.name || !signerForm.email}
            className="px-4 py-2.5 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary-strong disabled:opacity-50"
          >
            {saving ? 'Enviando...' : replaceMode ? 'Trocar e Reenviar' : 'Enviar para Assinatura'}
          </button>
        </div>
      }
    >
          <p className="text-sm text-slate-400">
            {replaceMode
              ? <>O documento <span className="text-white font-medium">{singleDoc?.name}</span> sera cancelado na ClickSign e reenviado com o novo signatario. O PDF continua o mesmo.</>
              : singleDoc
                ? <>O documento <span className="text-white font-medium">{singleDoc.name}</span> sera enviado para assinatura via ClickSign.</>
                : <>Documentos serao enviados para assinatura digital via <span className="text-white font-medium">ClickSign</span>.</>
            }
          </p>

          {/* Seleção de documentos com modo por linha (só quando enviando "todos") */}
          {!singleDoc && (
            <div className="space-y-3">
              <div>
                <div className="flex items-center justify-between mb-2 gap-2">
                  <label className="block text-sm font-medium text-slate-300">Documentos e modo de envio</label>
                  <div className="flex gap-1.5 text-[11px]">
                    <button
                      type="button"
                      onClick={() => setSelectedIds(new Set(linhas.map((l) => l.id)))}
                      className="px-2 py-0.5 text-purple-400 hover:text-purple-300"
                    >
                      Marcar todos
                    </button>
                    <button
                      type="button"
                      onClick={() => setSelectedIds(new Set())}
                      className="px-2 py-0.5 text-slate-400 hover:text-slate-300"
                    >
                      Desmarcar
                    </button>
                  </div>
                </div>
                <div className="max-h-72 overflow-y-auto space-y-1.5 pr-1 border border-central-borda rounded-lg p-2 bg-central-fundo/40">
                  {linhas.length === 0 ? (
                    <div className="text-xs text-slate-500 italic text-center py-3">Nenhum documento em status "gerado" disponível para envio</div>
                  ) : linhas.map((item) => {
                    const checked = selectedIds.has(item.id);
                    const currentMode = docMode.get(item.id) || (item.isProposta ? 'separate' : 'unified');
                    return (
                      <div key={item.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-800/60 group">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => {
                            const next = new Set(selectedIds);
                            if (e.target.checked) next.add(item.id);
                            else next.delete(item.id);
                            setSelectedIds(next);
                          }}
                          className="accent-purple-500"
                        />
                        <span className="text-sm text-slate-200 flex-1 truncate">{item.label}</span>
                        {item.isProposta && <span className="text-[10px] px-1.5 py-0.5 bg-amber-600/20 text-amber-400 rounded">proposta</span>}
                        {/* Toggle Unificar / Separado */}
                        <div className={`inline-flex rounded overflow-hidden border ${checked ? 'border-slate-600' : 'border-central-borda opacity-40'}`}>
                          <button
                            type="button"
                            disabled={!checked}
                            onClick={() => {
                              const next = new Map(docMode);
                              next.set(item.id, 'unified');
                              setDocMode(next);
                            }}
                            className={`text-[10px] px-2 py-0.5 transition-colors ${currentMode === 'unified' ? 'bg-purple-600/40 text-purple-200' : 'bg-slate-800 text-slate-400 hover:bg-slate-700'}`}
                            title="Será incluído no PDF unificado"
                          >
                            🔗 Unificar
                          </button>
                          <button
                            type="button"
                            disabled={!checked}
                            onClick={() => {
                              const next = new Map(docMode);
                              next.set(item.id, 'separate');
                              setDocMode(next);
                            }}
                            className={`text-[10px] px-2 py-0.5 transition-colors ${currentMode === 'separate' ? 'bg-amber-600/40 text-amber-200' : 'bg-slate-800 text-slate-400 hover:bg-slate-700'}`}
                            title="Será enviado como documento separado"
                          >
                            📄 Separado
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {/* Resumo */}
                <div className="text-[11px] text-slate-400 mt-2 px-2 py-1.5 bg-central-fundo/60 rounded border border-central-borda/60">
                  <div className="flex justify-between">
                    <span>🔗 Unificados: <strong className="text-purple-300">{unifiedCount}</strong> item(s) {unifiedCount > 0 ? '→ 1 PDF' : ''}</span>
                    <span>📄 Separados: <strong className="text-amber-300">{separateCount}</strong> item(s)</span>
                  </div>
                  <div className="text-slate-500 mt-1">Total ClickSign: <strong className="text-slate-300">{totalDocs}</strong> documento(s), {totalDocs} assinatura(s)</div>
                </div>
              </div>
            </div>
          )}

          {/* Atalhos dos sócios — mesma fonte única do modal de ofícios (config/company.ts).
              Digitar na mão é o que gerou grafia divergente de nome/CPF no passado. */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Signatario</label>
            <div className="flex gap-2">
              {(['fabio', 'heitor'] as const).map((k) => {
                const s = SIGNERS[k];
                const ativo = signerForm.email === s.email;
                return (
                  <button
                    key={k}
                    type="button"
                    onClick={() => setSignerForm({ ...signerForm, name: s.name, email: s.email, cpf: s.cpf, birthday: s.birthday, phone: s.phone || '' })}
                    className={`px-3 py-2 text-xs rounded-lg border transition-colors ${ativo ? 'bg-slate-700 border-orange-500 text-white' : 'bg-slate-900 border-slate-600 text-slate-300 hover:border-slate-500'}`}
                  >
                    {s.name} <span className="opacity-60">({s.cargo})</span>
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Nome do Signatario *</label>
            <input type="text" value={signerForm.name} onChange={(e) => setSignerForm({ ...signerForm, name: e.target.value })} className={`w-full px-4 py-2.5 ${campo} rounded-lg focus:outline-none`} />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Email *</label>
            <input type="email" value={signerForm.email} onChange={(e) => setSignerForm({ ...signerForm, email: e.target.value })} className={`w-full px-4 py-2.5 ${campo} rounded-lg focus:outline-none`} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">CPF</label>
              <input type="text" value={signerForm.cpf} onChange={(e) => setSignerForm({ ...signerForm, cpf: e.target.value })} className={`w-full px-4 py-2.5 ${campo} rounded-lg focus:outline-none`} />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Nascimento</label>
              <input type="date" value={signerForm.birthday} onChange={(e) => setSignerForm({ ...signerForm, birthday: e.target.value })} className={`w-full px-4 py-2.5 ${campo} rounded-lg focus:outline-none`} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Metodo de Assinatura</label>
              <select value={signerForm.authType} onChange={(e) => setSignerForm({ ...signerForm, authType: e.target.value as AuthType })} className={`w-full px-4 py-2.5 ${campo} rounded-lg focus:outline-none`}>
                <option value="email">Codigo por Email</option>
                <option value="whatsapp">Codigo por WhatsApp</option>
                <option value="icp_brasil">Certificado Digital (ICP-Brasil)</option>
              </select>
            </div>
          </div>
          {signerForm.authType === 'whatsapp' && (
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">WhatsApp *</label>
              <input type="text" value={signerForm.phone} onChange={(e) => setSignerForm({ ...signerForm, phone: e.target.value })} placeholder="(45) 99999-9999" className={`w-full px-4 py-2.5 ${campo} rounded-lg focus:outline-none`} />
            </div>
          )}
    </CascaDeModal>
    </div>
  );
}
